import { useMutation, UseMutationResult, useQuery, UseQueryResult, useQueryClient } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import ResponseError from "#/types/responseError"
import { MemberProviderSelection, UpsertMemberProviderSelectionRequest } from "#/types/memberProviderSelection"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"

const MEMBER_PROVIDER_SELECTION_QUERY_KEY = "memberProviderSelectionQueryKey"

/**
 * Fetches the member's provider selection for the given routing info. Used by the clinic and OBGYN name steps.
 */
export function useMemberProviderSelectionQuery(
  routingInfoId?: number
): UseQueryResult<MemberProviderSelection, ResponseError> {
  return useQuery(
    [MEMBER_PROVIDER_SELECTION_QUERY_KEY, routingInfoId],
    async () => {
      if (!routingInfoId) {
        return null
      }
      try {
        return await carrotClient.getMemberProviderSelectionByRoutingInfoId(routingInfoId)
      } catch (error) {
        // 404 means no record exists yet for this routing info.
        // Return null (success state) so React Query caches the result
        // and stops refetching, rather than looping on error state.
        if ((error as ResponseError)?.statusCode === HttpStatusCodes.NOT_FOUND) {
          return null
        }
        throw error
      }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: buildCarrotApiRetryPolicy(1),
      enabled: !!routingInfoId
    }
  )
}

/**
 * Upserts member provider selection. On success, refetches the member provider selection query for the same routingInfoId.
 */
export function useMemberProviderSelectionMutation(): UseMutationResult<
  MemberProviderSelection,
  ResponseError,
  UpsertMemberProviderSelectionRequest
> {
  const queryClient = useQueryClient()
  return useMutation(
    async (payload: UpsertMemberProviderSelectionRequest) => {
      return carrotClient.upsertMemberProviderSelection(payload)
    },
    {
      // Use setQueryData instead of refetchQueries so the mutation result is
      // written to the cache synchronously without triggering isFetching=true.
      // This eliminates the post-submit loading flash on WhatKindOfProvider /
      // NameOfClinic / NameOfObGyn steps.
      onSuccess: (data, variables) =>
        queryClient.setQueryData([MEMBER_PROVIDER_SELECTION_QUERY_KEY, variables.routingInfoId], data),
      retry: buildCarrotApiRetryPolicy(1)
    }
  )
}
